<template>
  <WebsiteCmsShell
    :title="pageTitle"
    :subtitle="pageSubtitle"
    :list-eyebrow="modeLabel"
    :list-title="contentTitle"
    :list-description="contentDescription"
  >
    <template #list>
      <div class="website-crud-form">
        <component
          v-if="ready"
          :is="formComponent"
          :form="form"
          :route="submitRoute"
        />
        <div v-else class="website-crud-form__loading">
          <b-spinner small variant="primary" />
          <span>Memuat data...</span>
        </div>
      </div>
    </template>
  </WebsiteCmsShell>
</template>

<script>
import CrudModule from "@/core/modules/CrudModule.js";
import WebsiteCmsShell from "@/component/websites/WebsiteCmsShell.vue";
import SettingForm from "@/component/websites/SettingForm.vue";
import SettingFormEdit from "@/component/websites/SettingFormEdit.vue";
import SettingFormDetail from "@/component/websites/SettingFormDetail.vue";
import SlideForm from "@/component/websites/SlideForm.vue";
import SlideFormEdit from "@/component/websites/SlideFormEdit.vue";
import SlideFormDetail from "@/component/websites/SlideFormDetail.vue";
import ServiceForm from "@/component/websites/ServiceForm.vue";
import ServiceFormEdit from "@/component/websites/ServiceFormEdit.vue";
import ServiceFormDetail from "@/component/websites/ServiceFormDetail.vue";
import PostForm from "@/component/websites/PostForm.vue";
import PostFormEdit from "@/component/websites/PostFormEdit.vue";
import PostFormDetail from "@/component/websites/PostFormDetail.vue";
import DoctorForm from "@/component/websites/DoctorForm.vue";
import DoctorFormEdit from "@/component/websites/DoctorFormEdit.vue";
import DoctorFormDetail from "@/component/websites/DoctorFormDetail.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";

const configs = {
  setting: {
    label: "Setelan Website", list: "/website/setting", endpoint: "website-settings",
    description: "Kelola identitas, kontak, dan kanal sosial website klinik.",
    empty: { title: "", address: "", phone: "", emergency: "", support: "", email: "", text_1: "", text_2: "", text_3: "", facebook_id: "", twitter_id: "", instagram_id: "", youtube_id: "", linkedin_id: "", status: false, logo: [] },
    forms: { add: SettingForm, edit: SettingFormEdit, detail: SettingFormDetail }, events: ["updateWebsiteSetting", "cancelWebsiteSettingUpdate"],
  },
  slide: {
    label: "Slide Banner", list: "/website/slide", endpoint: "website-slides",
    description: "Atur banner utama dan urutan informasi yang tampil di beranda website.",
    empty: { title: "", text_1: "", text_2: "", text_3: "", order: "", status: false, button: false, image: [] },
    forms: { add: SlideForm, edit: SlideFormEdit, detail: SlideFormDetail }, events: ["updateWebsiteSlide", "cancelAddWebsiteSlide"],
  },
  service: {
    label: "Layanan", list: "/website/service", endpoint: "website-services",
    description: "Kelola layanan unggulan yang ditampilkan kepada pasien.",
    empty: { title: "", description: "", status: false, order: "", image: [] },
    forms: { add: ServiceForm, edit: ServiceFormEdit, detail: ServiceFormDetail }, events: ["updateWebsiteService", "cancelAddWebsiteService", "cancelWebsiteServiceUpdate"],
  },
  posts: {
    label: "Berita", list: "/website/posts", endpoint: "website-posts",
    description: "Tulis dan kelola informasi kesehatan serta kabar terbaru klinik.",
    empty: { title: "", excerpt: "", content: "", date: "", author: "", tags: "", post_category_id: "", image: [], status: false, yt_link: "" },
    forms: { add: PostForm, edit: PostFormEdit, detail: PostFormDetail }, events: ["updateWebsitePost", "cancelAddWebsitePost"],
  },
  doctor: {
    label: "Dokter Spesialis", list: "/website/doctor", endpoint: "website-doctors",
    description: "Kelola profil dokter yang tampil pada website klinik.",
    empty: { name: "", specialty: "", description: "", image: [], order: null, status: false },
    forms: { add: DoctorForm, edit: DoctorFormEdit, detail: DoctorFormDetail }, events: ["updateWebsiteDoctor", "cancelAddWebsiteDoctor", "cancelWebsiteDoctorUpdate"],
  },
};

export default {
  name: "WebsiteCrudForm",
  components: { WebsiteCmsShell },
  data() { return { form: {}, ready: false, eventHandlers: [] }; },
  computed: {
    type() { return this.$route.meta.websiteType; },
    mode() { return this.$route.meta.websiteMode; },
    config() { return configs[this.type]; },
    formComponent() { return this.config.forms[this.mode]; },
    submitRoute() { return this.mode === "add" ? this.config.endpoint : `${this.config.endpoint}/${this.$route.params.id}`; },
    modeLabel() { return this.mode === "add" ? "Tambah data" : this.mode === "edit" ? "Ubah data" : "Detail data"; },
    pageTitle() { return `${this.mode === "add" ? "Tambah" : this.mode === "edit" ? "Edit" : "Detail"} ${this.config.label}`; },
    pageSubtitle() { return this.config.description; },
    contentTitle() { return this.mode === "detail" ? `Informasi ${this.config.label}` : `Form ${this.config.label}`; },
    contentDescription() { return this.mode === "detail" ? "Periksa data yang dipilih sebelum kembali ke daftar." : "Lengkapi kolom yang diperlukan, lalu simpan perubahan."; },
  },
  async created() {
    this.form = { ...this.config.empty };
    if (this.type === "posts" && !this.form.date) this.form.date = window.medhopToday();
    if (this.mode !== "add") {
      const data = await CrudModule.get(`${this.config.endpoint}/${this.$route.params.id}`);
      if (!data) { this.$router.replace(this.config.list); return; }
      this.form = { ...data, status: data.status == 1, _method: "put" };
      if (Object.prototype.hasOwnProperty.call(data, "button")) this.form.button = data.button == 1;
    }
    this.ready = true;
    this.config.events.forEach((event) => {
      const handler = () => this.$router.push(this.config.list);
      this.$root.$on(event, handler);
      this.eventHandlers.push({ event, handler });
    });
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Website & CMS", route: this.config.list },
      { title: this.config.label, route: this.config.list },
      { title: this.modeLabel },
    ]);
  },
  beforeDestroy() { this.eventHandlers.forEach(({ event, handler }) => this.$root.$off(event, handler)); },
};
</script>

<style scoped>
.website-crud-form { margin: 0; max-width: none; width: 100%; }
.website-crud-form__loading { align-items: center; color: #7c899e; display: flex; gap: 10px; justify-content: center; min-height: 220px; }
</style>
